<script setup>
import {
  computed,
  onMounted,
  ref,
  provide,
  defineAsyncComponent,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';

// 异步加载子组件（按需加载，优化性能）
const WechatCrawlLocalEntryTab = defineAsyncComponent(
  () => import('./localComponents/WechatCrawlLocalEntryTab.vue')
);
const WechatCrawlLocalRulesTab = defineAsyncComponent(
  () => import('./localComponents/WechatCrawlLocalRulesTab.vue')
);
const WechatCrawlLocalHistoryTab = defineAsyncComponent(
  () => import('./localComponents/WechatCrawlLocalHistoryTab.vue')
);
const WechatCrawlLocalDocsTab = defineAsyncComponent(
  () => import('./localComponents/WechatCrawlLocalDocsTab.vue')
);

const route = useRoute();
const router = useRouter();
const store = useStore();
const { t } = useI18n();

// 提供共享数据给子组件（inject 使用）
provide('store', store);
provide('route', route);
provide('router', router);

// 从路由获取知识库 ID
const knowledgeBaseId = computed(() => Number(route.params.id));

// Tab 名称映射（与 URL 路径参数对应）
const TAB_NAMES = ['entry', 'rules', 'history', 'docs'];

// 从 URL 路径参数获取初始 tab
const getInitialTab = () => {
  const pageParam = route.params.page;
  const tabIndex = TAB_NAMES.indexOf(pageParam);
  return tabIndex !== -1 ? tabIndex : 0;
};

// Tab 切换
const activeTab = ref(getInitialTab());
const tabs = computed(() => [
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.ENTRY') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.RULES') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.HISTORY') },
  { label: t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.TABS.DOCS') },
]);

const handleTabChanged = tab => {
  const tabIndex = tabs.value.indexOf(tab);
  if (tabIndex !== -1) {
    activeTab.value = tabIndex;
    // 更新 URL 路径
    router.push({
      name: 'pilot_documents_wechat_crawl_page',
      params: { id: knowledgeBaseId.value, page: TAB_NAMES[tabIndex] },
    });
  }
};

// 导航到指定 tab
const navigateToTab = (tabIndex, query = {}) => {
  if (tabIndex >= 0 && tabIndex < TAB_NAMES.length) {
    activeTab.value = tabIndex;
    router.push({
      name: 'pilot_documents_wechat_crawl_page',
      params: { id: knowledgeBaseId.value, page: TAB_NAMES[tabIndex] },
      query,
    });
  }
};

// 监听路由参数变化，同步更新 activeTab
watch(
  () => route.params.page,
  newPage => {
    const tabIndex = TAB_NAMES.indexOf(newPage);
    if (tabIndex !== -1 && activeTab.value !== tabIndex) {
      activeTab.value = tabIndex;
    }
  }
);

// 知识库信息
const knowledgeBase = ref(null);

// 抓取统计
const stats = ref({ pending: 0, success: 0, failed: 0, filtered: 0 });

// 规则状态
const hasRule = ref(false);
const ruleEnabled = ref(false);

// 加载状态
const isLoading = ref(false);

// RulesTab 组件引用
const rulesTabRef = ref(null);

// 获取知识库详情
const fetchKnowledgeBase = async () => {
  isLoading.value = true;
  try {
    const response = await store.dispatch('pilotKnowledgeBases/show', {
      id: knowledgeBaseId.value,
    });
    knowledgeBase.value = response.payload || response;
  } catch (error) {
    console.error('Failed to fetch knowledge base:', error);
  } finally {
    isLoading.value = false;
  }
};

// 获取抓取统计和规则状态
const fetchStats = async () => {
  try {
    const response = await store.dispatch('pilotWechatCrawl/show', {
      knowledge_base_id: knowledgeBaseId.value,
      scope: 'local',
    });
    if (response.stats) {
      stats.value = response.stats;
    }
    // 获取规则状态
    if (response.rule) {
      hasRule.value = true;
      ruleEnabled.value = response.rule.enabled !== false;
    } else {
      hasRule.value = false;
      ruleEnabled.value = false;
    }
  } catch (error) {
    console.error('Failed to fetch stats:', error);
  }
};

// 返回列表页
const goBack = () => {
  router.push({
    name: 'pilot_knowledge_bases_documents',
    params: { id: knowledgeBaseId.value },
  });
};

// 处理 EntryTab 事件
const handleNavigateToRules = () => {
  navigateToTab(1);
};

const handleNavigateToHistory = (query = {}) => {
  navigateToTab(2, query);
};

// 处理 RulesTab 保存事件
const handleRulesSave = () => {
  fetchStats();
};

// 保存配置
const saveConfig = async () => {
  await rulesTabRef.value?.saveConfig();
};

onMounted(async () => {
  // 获取运行模式状态（用于外部服务 URL）
  await store.dispatch('pilotWechatCrawlRunMode/fetch');
  // 获取知识库详情
  await fetchKnowledgeBase();
  // 获取统计数据
  await fetchStats();
});
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-slate-white dark:bg-n-slate-800">
    <!-- Header -->
    <header class="sticky top-0 z-10 px-6 bg-n-slate-white dark:bg-n-slate-800">
      <div class="w-full mx-auto max-w-[60rem]">
        <div class="flex items-center justify-between h-20">
          <div class="flex items-center">
            <button
              type="button"
              class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
              @click="goBack"
            >
              <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
            </button>
            <h1
              class="ml-4 text-lg font-semibold text-n-slate-900 dark:text-n-slate-100"
            >
              {{ t('PILOT.KNOWLEDGE_BASES.SIDEBAR.WECHAT_CRAWL_TITLE') }}
            </h1>
          </div>
          <!-- Tab Bar -->
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTab"
            @tab-changed="handleTabChanged"
          />
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1 w-full px-6 overflow-y-auto">
      <div class="w-full mx-auto max-w-[60rem] py-6 flex flex-col gap-6">
        <!-- Tab: 抓取入口 -->
        <template v-if="activeTab === 0">
          <WechatCrawlLocalEntryTab
            :active="activeTab === 0"
            :knowledge-base="knowledgeBase"
            :stats="stats"
            :has-rule="hasRule"
            :rule-enabled="ruleEnabled"
            @navigate-to-rules="handleNavigateToRules"
            @navigate-to-history="handleNavigateToHistory"
          />
        </template>

        <!-- Tab: 图文接收配置 -->
        <template v-else-if="activeTab === 1">
          <WechatCrawlLocalRulesTab
            ref="rulesTabRef"
            :active="activeTab === 1"
            :knowledge-base="knowledgeBase"
            @save="handleRulesSave"
          />
        </template>

        <!-- Tab: 历史记录 -->
        <template v-else-if="activeTab === 2">
          <WechatCrawlLocalHistoryTab
            :active="activeTab === 2"
            :knowledge-base="knowledgeBase"
          />
        </template>

        <!-- Tab: 开发文档 -->
        <template v-else-if="activeTab === 3">
          <WechatCrawlLocalDocsTab :knowledge-base="knowledgeBase" />
        </template>
      </div>
    </main>

    <!-- 底部操作按钮（仅规则 Tab 显示） -->
    <footer
      v-if="activeTab === 1"
      class="sticky bottom-0 z-10 px-6 py-4 bg-n-slate-white dark:bg-n-slate-800 border-t border-n-weak"
    >
      <div class="w-full mx-auto max-w-[60rem] flex items-center justify-end">
        <Button
          :label="t('PILOT.KNOWLEDGE_BASES.DOCUMENTS.WECHAT_CRAWL.SAVE')"
          blue
          :is-loading="rulesTabRef?.isSaving"
          @click="saveConfig"
        />
      </div>
    </footer>
  </div>
</template>
